import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import {
  fetchProductsHistoryReportAction,
  setLoading,
} from "../slices/productsHistoryReportSlice";
import { FetchProductsHistoryReportParams } from "../api/fetchProductsHistoryReport/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

interface Pagination {
  pageIndex: number;
  pageSize: number;
}

export interface ProductsHistoryReportFilters {
  searchTerm?: string;
  companyIds?: number[];
  categoryIds?: number[];
  statuses?: string[];
  orderDateFrom?: string;
  orderDateTo?: string;
}

function buildFilterSortParams(
  filters: ProductsHistoryReportFilters,
  sorting: SortingState,
): Omit<FetchProductsHistoryReportParams, "page" | "pageSize"> {
  const params: Omit<FetchProductsHistoryReportParams, "page" | "pageSize"> =
    {};
  if (filters.searchTerm) params.searchTerm = filters.searchTerm;
  if (filters.companyIds?.length) params.companyIds = filters.companyIds;
  if (filters.categoryIds?.length) params.categoryIds = filters.categoryIds;
  if (filters.statuses?.length) params.statuses = filters.statuses;
  if (filters.orderDateFrom) params.orderDateFrom = filters.orderDateFrom;
  if (filters.orderDateTo) params.orderDateTo = filters.orderDateTo;
  if (sorting.length > 0) {
    params.sortBy = sorting[0].id;
    params.sortOrder = sorting[0].desc ? "DESC" : "ASC";
  }
  return params;
}

export function useProductsHistoryReport() {
  const dispatch = useAppDispatch();

  const { items, totalCount, loading, error } = useSelector(
    (state: RootState) => state.productsHistoryReport,
  );

  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 20,
  });

  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<ProductsHistoryReportFilters>({});

  useEffect(() => {
    // Cancel the in-flight request whenever the params change (or the page
    // unmounts) so a slow earlier response can't overwrite a newer one.
    const controller = new AbortController();
    dispatch(
      fetchProductsHistoryReportAction(
        {
          page: pagination.pageIndex + 1,
          pageSize: pagination.pageSize,
          ...buildFilterSortParams(filters, sorting),
        },
        controller.signal,
      ),
    );
    return () => controller.abort();
  }, [dispatch, pagination, sorting, filters]);

  const handlePaginationChange = useCallback(
    (newPagination: Pagination) => {
      dispatch(setLoading(true));
      setPagination(newPagination);
    },
    [dispatch],
  );

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      dispatch(setLoading(true));
      setSorting((prev) =>
        typeof updater === "function" ? updater(prev) : updater,
      );
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch],
  );

  const handleFilterChange = useCallback(
    (
      key: keyof ProductsHistoryReportFilters,
      value: string | string[] | number[] | undefined,
    ) => {
      dispatch(setLoading(true));
      setFilters((prev) => ({ ...prev, [key]: value }));
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch],
  );

  return {
    items,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    exportParams: buildFilterSortParams(filters, sorting),
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
}
